import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, ChevronRight, List } from 'lucide-react';
import { SidebarMenu, SidebarMenuItem, SidebarMenuButton, SidebarSeparator, useSidebar } from 'src/shared/components/ui/sidebar';
import { Popover, PopoverContent, PopoverTrigger } from 'src/shared/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from 'src/shared/components/ui/command';
import useCustomer from 'src/app/customers/hooks/useCustomer';
import usePermissions from 'src/shared/hooks/usePermissions';
import { useAppSelector } from 'src/store';
import { getAdminRoutesAvailability } from 'src/routes/state/routesSelector';
import { navRoutesConfig } from 'src/shared/data/navRoutesConfig';
import { AdminViews } from 'src/routes/routesConfig';

const OrganisationMenu = () => {
    const [open, setOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const { allCustomers, currentCustomerName, switchCustomer } = useCustomer();
    const { isCurrentUserInternal } = usePermissions();
    // Only link where the reader can actually go; the same flag opens the page itself.
    const canReachOrganisationsPage = useAppSelector(getAdminRoutesAvailability).organisations;
    const { state, isMobile } = useSidebar();
    // On the icon-only rail there is no room for the name or the chevron, so the
    // tooltip carries the current organisation instead.
    const isIconOnly = state === 'collapsed' && !isMobile;

    const customers = allCustomers?.customers || [];

    const getCustomerLabel = (customer: { customerName: string; customerCode: string }) =>
        isCurrentUserInternal ? `${customer.customerName} (${customer.customerCode})` : customer.customerName;

    const filteredCustomers = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        if (!query) {
            return customers;
        }
        return customers.filter(customer => getCustomerLabel(customer).toLowerCase().includes(query));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customers, searchQuery, isCurrentUserInternal]);

    // Whoever can see every organisation needs a way through even when assigned one — or
    // none; for everyone else a single choice is not worth a control.
    if (customers.length <= 1 && !canReachOrganisationsPage) {
        return null;
    }

    const handleOpenChange = (nextOpen: boolean) => {
        setOpen(nextOpen);
        if (!nextOpen) {
            setSearchQuery('');
        }
    };

    const handleSelect = (customerId: string) => {
        switchCustomer(customerId);
        setOpen(false);
        setSearchQuery('');
    };

    return (
        <>
            <SidebarMenu>
                <SidebarMenuItem>
                    <Popover open={open} onOpenChange={handleOpenChange}>
                        <PopoverTrigger asChild>
                            <SidebarMenuButton
                                tooltip={currentCustomerName || 'Switch organisation'}
                                data-testid='organisation-menu-button'
                            >
                                <Building2 />
                                {!isIconOnly && (
                                    <>
                                        <span className='truncate'>{currentCustomerName || 'Select organisation'}</span>
                                        <ChevronRight className='ml-auto h-4 w-4 shrink-0' />
                                    </>
                                )}
                            </SidebarMenuButton>
                        </PopoverTrigger>
                        <PopoverContent side='right' align='start' className='w-[260px] p-0' collisionPadding={{ bottom: 20 }}>
                            <Command shouldFilter={false}>
                                <CommandInput placeholder='Search organisations' value={searchQuery} onValueChange={setSearchQuery} />
                                <CommandList>
                                    <CommandEmpty>No results.</CommandEmpty>
                                    <CommandGroup>
                                        {filteredCustomers.map(customer => (
                                            <CommandItem
                                                key={customer.customerId}
                                                value={getCustomerLabel(customer)}
                                                onSelect={() => handleSelect(customer.customerId)}
                                            >
                                                {getCustomerLabel(customer)}
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                </CommandList>
                                {canReachOrganisationsPage && (
                                    <div className='flex flex-col border-t border-grey-20 p-1'>
                                        <Link
                                            to={navRoutesConfig[AdminViews.ORGANISATIONS]}
                                            onClick={() => handleOpenChange(false)}
                                            data-testid='organisation-menu-see-all'
                                            className='flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-grey-10'
                                        >
                                            <List size={16} className='shrink-0' />
                                            See all organisations
                                        </Link>
                                    </div>
                                )}
                            </Command>
                        </PopoverContent>
                    </Popover>
                </SidebarMenuItem>
            </SidebarMenu>
            <SidebarSeparator />
        </>
    );
};

export default OrganisationMenu;
